// Application configuration

const PUBLIC_AUTH_CHALLENGE_PROVIDERS = new Set(["turnstile"]);

type AppEnvironment = "development" | "production" | "staging";

const getPublicAuthChallengeProvider = (
  value: string | undefined,
): "turnstile" => {
  if (value && PUBLIC_AUTH_CHALLENGE_PROVIDERS.has(value)) {
    return "turnstile";
  }

  return "turnstile";
};

const getPositiveInteger = (
  value: string | undefined,
  fallback: number,
): number => {
  const parsed = Number.parseInt(value ?? "", 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const getAppEnvironment = (value: string | undefined): AppEnvironment => {
  if (value === "production" || value === "staging") {
    return value;
  }

  return "development";
};

// Environment variables with defaults
export const config = {
  // API Configuration
  api: {
    baseUrl: import.meta.env.VITE_API_BASE_URL || "/backend-api/api/v1",
    dataServiceUrl: import.meta.env.VITE_DATA_SERVICE_URL || "/data-api",
    agentServiceUrl: import.meta.env.VITE_AGENT_SERVICE_URL || "/agent-api",
    knowledgeServiceUrl: import.meta.env.VITE_KNOWLEDGE_SERVICE_URL || "/knowledge-service",
    timeout: getPositiveInteger(import.meta.env.VITE_API_TIMEOUT, 150000), // 2.5 minutes - increased for AI generation (can take 60-120s with retries)
    retryAttempts: getPositiveInteger(
      import.meta.env.VITE_API_RETRY_ATTEMPTS,
      3,
    ),
    retryDelay: getPositiveInteger(import.meta.env.VITE_API_RETRY_DELAY, 1000),
  },

  // WebSocket Configuration
  websocket: {
    url: import.meta.env.VITE_WS_URL || "/backend-api", // Same-origin via nginx/vite-proxy /backend-api → backend WS endpoint
    reconnectAttempts: getPositiveInteger(
      import.meta.env.VITE_WS_RECONNECT_ATTEMPTS,
      5,
    ),
    reconnectInterval: getPositiveInteger(
      import.meta.env.VITE_WS_RECONNECT_INTERVAL,
      3000,
    ),
    cors: {
      origin: "http://localhost:3001", // Frontend runs on 3001
      methods: ["GET", "POST"],
      allowedHeaders: ["Content-Type", "Authorization"],
      credentials: true,
    },
  },

  // Auth0 Configuration
  auth0: {
    domain: "",
    clientId: "",
    audience: "",
    redirectUri: typeof window !== "undefined" ? window.location.origin : "",
  },

  auth: {
    publicChallenge: {
      enabled: import.meta.env.VITE_PUBLIC_AUTH_CHALLENGE_ENABLED === "true",
      provider: getPublicAuthChallengeProvider(
        import.meta.env.VITE_PUBLIC_AUTH_CHALLENGE_PROVIDER,
      ),
      turnstileSiteKey:
        import.meta.env.VITE_PUBLIC_AUTH_CHALLENGE_TURNSTILE_SITE_KEY?.trim() ||
        undefined,
      tokenTimeoutMs: getPositiveInteger(
        import.meta.env.VITE_PUBLIC_AUTH_CHALLENGE_TIMEOUT_MS,
        10000,
      ),
    },
  },

  // Application Configuration
  app: {
    name: "TerraMatrix",
    version: "1.0.0",
    environment: getAppEnvironment(import.meta.env.VITE_APP_ENV),
    debug: true,
  },

  // Editor Configuration
  editor: {
    // Default viewport dimensions
    viewports: {
      desktop: { width: 1920, height: 1080, name: "Desktop" },
      tablet: { width: 768, height: 1024, name: "Tablet" },
      mobile: { width: 375, height: 667, name: "Mobile" },
    },
    // Grid settings
    grid: {
      size: 10,
      enabled: true,
      snapToGrid: true,
    },
    // Auto-save settings
    autoSave: {
      enabled: true,
      interval: 30000, // 30 seconds
    },
    // Canvas settings
    canvas: {
      minZoom: 0.1,
      maxZoom: 5.0,
      defaultZoom: 0.6,
      zoomStep: 0.1,
    },
    // Component defaults
    components: {
      defaultWidth: 200,
      defaultHeight: 100,
      minWidth: 10,
      minHeight: 10,
      maxWidth: 2000,
      maxHeight: 2000,
    },
  },

  // Storage Configuration
  storage: {
    // Local storage keys
    keys: {
      user: "terramatrix_user",
      tokens: "terramatrix_tokens",
      preferences: "terramatrix_preferences",
      editorState: "terramatrix_editor_state",
      projects: "terramatrix_projects",
    },
    // Cache settings
    cache: {
      ttl: 300000, // 5 minutes
      maxSize: 100, // Maximum number of cached items
    },
  },

  // UI Configuration
  ui: {
    // Sidebar settings
    sidebar: {
      defaultWidth: 280,
      minWidth: 200,
      maxWidth: 400,
      collapsedWidth: 60,
    },
    // Right panel settings
    rightPanel: {
      defaultWidth: 320,
      minWidth: 250,
      maxWidth: 500,
    },
    // Notification settings
    notifications: {
      defaultDuration: 5000,
      maxVisible: 5,
      position: "top-right" as const,
    },
    // Animation settings
    animations: {
      duration: 200,
      easing: "ease-in-out",
      reducedMotion: false,
    },
  },

  // Performance Configuration
  performance: {
    // Virtual scrolling thresholds
    virtualScrolling: {
      itemHeight: 50,
      bufferSize: 10,
      threshold: 100,
    },
    // Debounce delays
    debounce: {
      search: 300,
      resize: 100,
      autoSave: 1000,
    },
    // Throttle delays
    throttle: {
      scroll: 16,
      mousemove: 16,
      cursor: 50,
    },
  },

  // Feature Flags
  features: {
    // Beta features
    aiAssistant: true,
    realTimeCollaboration: false, // Temporarily disabled to prevent connection errors
    advancedAnimations: true,
    codeGeneration: true,

    // Debug features
    debugMode: true,
    showPerformanceMetrics: true,
    enableLogging: true,
  },

  // Google Analytics Configuration
  analytics: {
    // GA4 Measurement ID (e.g., G-XXXXXXXXXX)
    measurementId: import.meta.env.VITE_GA_MEASUREMENT_ID || '',
    // Enable/disable analytics tracking
    enabled: import.meta.env.VITE_GA_ENABLED === 'true',
    // Enable debug mode for development testing
    debug: import.meta.env.VITE_GA_DEBUG === 'true',
    // Respect Do Not Track browser setting
    respectDoNotTrack: true,
    // Anonymize IP addresses for GDPR compliance
    anonymizeIp: true,
  },

  // Limits and Constraints
  limits: {
    // Project limits
    maxProjectsPerUser: 50,
    maxComponentsPerProject: 1000,
    maxProjectMembers: 20,

    // File limits
    maxFileSize: 10485760, // 10MB
    maxImageSize: 5242880, // 5MB

    // Text limits
    maxProjectNameLength: 100,
    maxProjectDescriptionLength: 1000,
    maxComponentNameLength: 100,
    maxUsernameLength: 30,

    // API limits
    maxRequestsPerMinute: 100,
    maxConcurrentRequests: 10,
  },
} as const;

// Type for configuration
export type Config = typeof config;

// Environment check utilities
export const isDevelopment = config.app.environment === "development";
export const isProduction = config.app.environment === "production";
export const isStaging = config.app.environment === "staging";

// Validation for required environment variables
export const validateConfig = (): void => {
  // Placeholder for environment validation
  console.log("Configuration validated");
};
